import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
  LinearProgress,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import { getJobStatus, cancelJob, type JobStatus } from '../api/videoApi';

interface ProcessingStatusComponentProps {
  jobId: string;
  isBenchmark?: boolean;
  onComplete?: (jobId: string) => void;
  onError?: (jobId: string, error: string) => void;
  pollInterval?: number;
}

export default function ProcessingStatusComponent({
  jobId,
  onComplete,
  onError,
  pollInterval = 2000,
}: ProcessingStatusComponentProps) {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const jobStatus = await getJobStatus(jobId);
      setStatus(jobStatus);
      setLoading(false);
      setError(null);

      // Handle completion
      if (jobStatus.status === 'completed' && onComplete) {
        onComplete(jobId);
      }

      // Handle errors
      if (jobStatus.status === 'failed' && onError) {
        onError(jobId, jobStatus.error || 'Processing failed');
      }
    } catch (err) {
      // If we already have a status (processing/pending), transient poll errors
      // are expected (e.g., 502 under GPU load). Keep polling silently.
      if (status?.status === 'processing' || status?.status === 'pending') {
        console.warn('Transient status poll error (will retry):', err);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
      setLoading(false);
    }
  }, [jobId, onComplete, onError, status?.status]);

  useEffect(() => {
    // Initial fetch
    fetchStatus();

    // Poll for updates if job is still processing
    const interval = setInterval(() => {
      if (status?.status === 'processing' || status?.status === 'pending') {
        fetchStatus();
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [fetchStatus, status?.status, pollInterval]);

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    try {
      await cancelJob(jobId);
      await fetchStatus(); // Refresh status
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel job');
    } finally {
      setCancelling(false);
    }
  }, [jobId, fetchStatus]);

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading job status...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!status) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">Job not found</Alert>
      </Paper>
    );
  }

  const getErrorCategoryLabel = (category?: string): string => {
    switch (category) {
      case 'gpu_oom':
        return 'GPU Out of Memory';
      case 'invalid_video':
        return 'Invalid Video Format';
      case 'model_load_failure':
        return 'Model Load Failure';
      case 's3_error':
        return 'Storage Error';
      default:
        return 'Error';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'processing':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Processing Status</Typography>
        <Typography variant="body2" color="text.secondary">
          Job ID: {jobId.substring(0, 8)}...
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {status.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.status === 'failed' && (
            <Chip
              label={getErrorCategoryLabel(status.error_category)}
              color="error"
              size="small"
              sx={{ mb: 1 }}
            />
          )}
          <Box>{status.error}</Box>
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Status: <strong>{getStatusText(status.status)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(status.progress * 100)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={status.progress * 100}
          color={getStatusColor(status.status) as any}
        />
      </Box>

      {status.message && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {status.message}
        </Typography>
      )}

      {status.model && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Model: {status.model} | Instance: {status.instance_type}
        </Typography>
      )}

      {(status.status === 'processing' || status.status === 'pending') && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={cancelling}
          fullWidth
        >
          {cancelling ? 'Cancelling...' : 'Cancel Job'}
        </Button>
      )}

      {status.status === 'completed' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Processing completed successfully!
        </Alert>
      )}
    </Paper>
  );
}

